import React, { useState, FormEvent, useEffect, useMemo } from 'react'
import { connect, useDispatch } from 'react-redux'
import { Stack, Paper, Container } from '@mui/material'
import ProgressButton from '../../Utils/ProgressButton'
import { entityTextField } from '../../Utils/EntityField'
import { AppState } from '../../../reducers/index'
import { ProductMeasurementDetailState } from '../../../reducers/product/productMeasurementDetail'
import { upsertProductMeasurement } from '../../../actions/product/productMeasurementUpsert'
import ProductMeasurement from '../../../models/ProductMeasurement'
import { useTranslation } from 'react-i18next'
import { ISTableTitle } from '../../Utils/ISTable'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { fetchProductMeasurement } from '../../../actions/product/productMeasurementDetail'
import { BackButton } from '../../Utils/BackButton'
import Selector from '../../Selector'
import { isNil } from 'ramda'
import { ProductMeasurementTypeListState } from '../../../reducers/product/productMeasurementTypeList'
import { fetchAllProductMeasurementTypes } from '../../../actions/product/productMeasurementTypeList'

interface Props {
  productMeasurement: ProductMeasurementDetailState
  productMeasurementType: ProductMeasurementTypeListState
}

const empty: ProductMeasurement = {
  id: null,
  code: '',
  description: '',
  order: 0,
  typeId: 0,
  typeName: '',
  ownerId: 0,
  ownerName: '',
  active: true
}

const ProductMeasurementUpsert: React.FC<Props> = ({ productMeasurement, productMeasurementType }) => {
  const [changed, setChanged] = useState<boolean>(false)
  const [productMeasurementInfo, setProductMeasurementInfo] = useState<ProductMeasurement>(productMeasurement.productMeasurement || empty)
  const [measurementType, setMeasurementType] = useState(productMeasurementType.productMeasurementTypes?.find((measurementType) => measurementType.id === productMeasurement.productMeasurement?.typeId)?.description || '')
  const { t } = useTranslation('product')
  const dispatch = useDispatch()
  const params = useParams<{ productMeasurementId: string }>()
  const history = useHistory()
  const location = useLocation()

  useEffect(() => {
    if (params.productMeasurementId) {
      dispatch(fetchProductMeasurement(params.productMeasurementId))
    }
  }, [dispatch, params.productMeasurementId])

  useEffect(() => {
    dispatch(fetchAllProductMeasurementTypes())
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setDataMeasurementType = (description: any): number | null => {
    if (description) {
      setMeasurementType(description)
      return productMeasurementType.productMeasurementTypes?.find((type) => type.description === description)?.id || 0
    }
    return null
  }

  const updateMeasurementType = (e: any) => {
    const description = e.target.value
    const measurementTypeId = setDataMeasurementType(description)
    if (measurementTypeId) {
      setProductMeasurementInfo({ ...productMeasurementInfo, typeId: measurementTypeId })
      setChanged(true)
    }
  }

  const change = (newProductMeasurementInfo: ProductMeasurement) => {
    setProductMeasurementInfo(newProductMeasurementInfo)
    setChanged(true)
  }

  const changeProductMeasurement = async (event: FormEvent) => {
    event.preventDefault()
    try {
      await dispatch(upsertProductMeasurement(productMeasurementInfo))
      setChanged(false)
      if (location.state)
        (location.state as any).update = !(location.state as any).update
      history.goBack()
    }
    catch {
    }
  }

  const selectorList = (arrayList: any[]) => {
    const list = arrayList?.filter(element => element.active)
    return list ? [...list.map(element => element.description), ''] : []
  }

  useEffect(() => {
    if (productMeasurement.productMeasurement) {
      setProductMeasurementInfo(params.productMeasurementId ? productMeasurement.productMeasurement : empty)
      setMeasurementType(params.productMeasurementId && productMeasurementType && productMeasurementType.productMeasurementTypes ? productMeasurementType.productMeasurementTypes!.find((measurementType) => measurementType.id === productMeasurement.productMeasurement?.typeId)!.description : '')
      //setMeasurementType(params.productMeasurementId ? productMeasurement.productMeasurement?.typeName : '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productMeasurement.productMeasurement, params.productMeasurementId])

  const ProductMeasurementInfoField = useMemo(() => entityTextField(change), [])

  return (
    <Container>
      <Paper style={{ borderRadius: "10px", margin: "10px" }}>
        <ISTableTitle title={t('measurement')} />
        <Paper style={{ padding: "10px" }}>
          <form onSubmit={changeProductMeasurement}>
            <Stack display="flex" flexDirection="row" flexGrow={1} >
              <ProductMeasurementInfoField
                style={{ width: "200px", marginRight: "10px" }}
                key="code"
                variant="outlined"
                label={t("code")}
                entity={productMeasurementInfo}
                attr="code"
                size="medium"
              />
              <ProductMeasurementInfoField
                //style={{ width: "100%"}}
                key="description"
                variant="outlined"
                label={t("description")}
                entity={productMeasurementInfo}
                attr="description"
                size="medium"
              />
            </Stack>
            <Stack display="flex" flexDirection="row" flexGrow={1} >
              <ProductMeasurementInfoField
                style={{ marginRight: "10px" }}
                key="order"
                variant="outlined"
                label={t("order")}
                entity={productMeasurementInfo}
                attr='order'
                size="medium"
              />
              <Selector
                key="active"
                label={t("active")}
                style={{ width: "100%" }}
                value={isNil(productMeasurementInfo.active) ? "" : Boolean(productMeasurementInfo.active)}
                textAdapter={(_value) => Boolean(_value) ? t("yes") : t("no")}
                menuItems={[true, false]}
              />
            </Stack>
            <Stack display="flex" flexDirection="row" flexGrow={1} >
              <Selector
                key="measurementType"
                label={t("measurementType")}
                menuItems={selectorList(productMeasurementType.productMeasurementTypes!)}
                onChange={updateMeasurementType}
                value={measurementType}
                style={{ width: '100%' }}
                required={true} />
            </Stack>
            <Stack display="flex" flexDirection='row' justifyContent="space-between">
              <BackButton />
              <ProgressButton state={productMeasurement} disabled={!changed}>
                {t('save')}
              </ProgressButton>
            </Stack>
          </form>
        </Paper>
      </Paper>
    </Container>

  )
}

const mapStateToProps = (state: AppState) => ({
  productMeasurement: state.productMeasurementDetail,
  productMeasurementType: state.productMeasurementTypeList
})

export default connect<Props, {}, {}, AppState>(mapStateToProps, {})(ProductMeasurementUpsert)
